import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Search, Check, FileText } from "lucide-react";

export default function BusinessClaims() {
  const [claim, setClaim] = useState({ brand: "", model: "", warranty_months: "", documentation_url: "", manual_note: "" });
  const [claimable, setClaimable] = useState(null);
  const [claims, setClaims] = useState([]);

  const load = () => api.get("/business/claims").then((r) => setClaims(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const searchClaimable = async () => {
    if (!claim.brand.trim()) return toast.error("Marque requise");
    const { data } = await api.get("/business/claimable", { params: { brand: claim.brand, model: claim.model || undefined } });
    setClaimable(data);
  };

  const doClaim = async () => {
    if (!claim.brand.trim()) return toast.error("Marque requise");
    const { data } = await api.post("/business/claim", {
      brand: claim.brand, model: claim.model || null,
      warranty_months: claim.warranty_months ? parseInt(claim.warranty_months) : null,
      documentation_url: claim.documentation_url || null, manual_note: claim.manual_note || null,
    });
    toast.success(`${data.matched} passeport(s) revendiqué(s)`);
    setClaimable(null);
    setClaim({ brand: "", model: "", warranty_months: "", documentation_url: "", manual_note: "" });
    load();
  };

  return (
    <div className="space-y-6" data-testid="business-claims">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Revendiquer des passeports</h1>
        <p className="text-muted-foreground text-sm mt-1">Retrouve les biens de tes clients correspondant à ta marque et pousse la garantie et la notice officielles — sans jamais accéder à leurs données personnelles.</p>
      </div>

      <div className="omy-glass rounded-2xl p-6 space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <Input data-testid="claim-brand-input" value={claim.brand} onChange={(e) => { setClaim({ ...claim, brand: e.target.value }); setClaimable(null); }} className="bg-black/30" placeholder="Marque (ex. Samsung)" />
          <Input data-testid="claim-model-input" value={claim.model} onChange={(e) => { setClaim({ ...claim, model: e.target.value }); setClaimable(null); }} className="bg-black/30" placeholder="Modèle (optionnel)" />
          <Button data-testid="claim-search-btn" onClick={searchClaimable} variant="outline" className="rounded-full"><Search size={16} className="mr-1.5" /> Rechercher</Button>
        </div>
        {claimable && (
          <div data-testid="claimable-result" className="rounded-2xl bg-black/20 p-4 space-y-3">
            <p className="text-sm"><span data-testid="claimable-total" className="text-indigo-300 font-semibold text-lg">{claimable.total}</span> passeport(s) correspondant à « {claimable.brand} »</p>
            {claimable.models.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {claimable.models.map((m, i) => (
                  <span key={i} className="text-xs bg-black/30 rounded-full px-2.5 py-1">{m.model} · {m.count}</span>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3 pt-1">
              <div><Label className="text-xs">Garantie officielle (mois)</Label><Input data-testid="claim-warranty-input" type="number" value={claim.warranty_months} onChange={(e) => setClaim({ ...claim, warranty_months: e.target.value })} className="mt-1 bg-black/30" placeholder="24" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Lien notice / documentation</Label><Input data-testid="claim-doc-input" value={claim.documentation_url} onChange={(e) => setClaim({ ...claim, documentation_url: e.target.value })} className="mt-1 bg-black/30" placeholder="https://…" /></div>
            </div>
            <Input data-testid="claim-note-input" value={claim.manual_note} onChange={(e) => setClaim({ ...claim, manual_note: e.target.value })} className="bg-black/30" placeholder="Message officiel (optionnel)" />
            <Button data-testid="claim-confirm-btn" onClick={doClaim} disabled={claimable.total === 0} className="bg-indigo-500 hover:bg-indigo-500/90 rounded-full w-full sm:w-auto"><Check size={16} className="mr-1.5" /> Revendiquer & pousser l'info officielle</Button>
          </div>
        )}
      </div>

      {claims.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Revendications</h3>
          {claims.map((c, i) => (
            <div key={c.id} data-testid={`claim-item-${i}`} className="flex items-center gap-2 rounded-xl bg-black/20 px-4 py-2.5 text-sm">
              <ShieldCheck size={15} className="text-indigo-300 shrink-0" />
              <span className="font-medium">{c.brand}{c.model ? ` · ${c.model}` : ""}</span>
              {c.warranty_months && <span className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck size={11} /> {c.warranty_months} mois</span>}
              {c.documentation_url && <FileText size={13} className="text-muted-foreground" />}
              <span className="ml-auto text-xs bg-indigo-500/15 text-indigo-300 rounded-full px-2 py-0.5">{c.matched} bien(s)</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
